import { useCallback, useId, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useLatestLoad } from '../../hooks/useLatestLoad';
import { useSubmit } from '../../hooks/useSubmit';
import { correctCustomerCredit, fetchCustomerCredit } from '../adminApi';
import { Panel } from '../ui/Panel';
import { Field } from '../ui/Field';
import { Skeleton } from '../../ui/Skeleton';
import { Spinner } from '../../ui/Spinner';
import { btnPrimary, btnSecondary, inputCls } from '../ui/styles';
import { received } from '../inbox/inboxRules';
import { useRequestId } from '../../hooks/useRequestId';
import { ledgerKindLabel, parseEuroInput, signedAmount, unitBalance } from '../vouchers/voucherLabels';
import type { RequestOptions } from '../../core/types/backend';
import type { LedgerUnit } from '../../core/types/vouchers';

/**
 * Tegoed van één klant: euro's en behandelingen apart (nooit bij elkaar opgeteld), met de laatste
 * bewegingen en een correctie met verplichte reden. Transactie: wachten op de server, met requestId.
 */
export function CreditPanel() {
  const {
    id = ''
  } = useParams();
  const load = useCallback((opts: RequestOptions) => fetchCustomerCredit(id, opts), [id]);
  const {
    data,
    setData,
    status,
    reload
  } = useLatestLoad(load);
  const [open, setOpen] = useState(false);
  return <Panel title="Tegoed" actions={data && !open ? <button type="button" onClick={() => setOpen(true)} className="text-[13px] text-ink underline underline-offset-4 hover:text-clay" data-id="element-1145" data-mp="3tyh4udxvqt3z">
            Corrigeren
          </button> : undefined} data-id="element-1144" data-mp="uhskwzvjv0wg">
      {status === 'error' && !data ? <div className="text-[14px] text-muted" data-id="element-1146" data-mp="brt4f1dryu4n">
          Kon het tegoed niet laden.{' '}
          <button type="button" onClick={reload} className="text-ink underline underline-offset-4" data-id="element-1147" data-mp="bpgfg49lnji3">
            Opnieuw
          </button>
        </div> : !data ? <div role="status" aria-label="Tegoed laden" className="space-y-2" data-id="element-1148" data-mp="1n5502ozds6fq">
          <Skeleton className="h-5 w-24 rounded" data-id="element-1149" data-mp="xhj2v9at1ggi" />
          <Skeleton className="h-5 w-32 rounded" data-id="element-1150" data-mp="sjrcwktq04ey" />
        </div> : <>
          <dl className="grid grid-cols-2 gap-3" data-id="element-1151" data-mp="sm3qklsg3sm7">
            <div className="rounded-md bg-sand px-3 py-2.5" data-id="element-1152" data-mp="17fywpk17v3tp">
              <dt className="text-[12px] text-muted" data-id="element-1153" data-mp="12hxawrceoqej">In euro’s</dt>
              <dd className="text-[16px] tabular-nums text-ink" data-id="element-1154" data-mp="12iov760jz1pi">{unitBalance('cents', data.balance.cents)}</dd>
            </div>
            <div className="rounded-md bg-sand px-3 py-2.5" data-id="element-1155" data-mp="9t9noq83jqyc">
              <dt className="text-[12px] text-muted" data-id="element-1156" data-mp="9ut4chxo040n">Behandelingen</dt>
              <dd className="text-[16px] tabular-nums text-ink" data-id="element-1157" data-mp="oie8mf6a09a5">{data.balance.treatment}</dd>
            </div>
          </dl>

          {open && <CorrectionForm customerId={id} onClose={() => setOpen(false)} onDone={next => {
        setData(next);
        setOpen(false);
        toast.success('Tegoed gecorrigeerd');
      }} data-id="element-1158" data-mp="jpcptw2y7pla" />}

          {data.ledger.length > 0 ? <ol className="mt-4 divide-y divide-line border-t border-line text-[13px]" data-id="element-1159" data-mp="27hc2mt4kcuwu">
              {[...data.ledger].sort((a, b) => b.at.localeCompare(a.at)).slice(0, 6).map(e => <li key={e.id} className="flex items-start justify-between gap-3 py-2.5" data-id="element-1160" data-mp="1xrk2ljdp39kz">
                    <span className="min-w-0" data-id="element-1161" data-mp="2lh4p2c924oz0">
                      <span className="block text-ink" data-id="element-1162" data-mp="2qfofaouoomx6">{ledgerKindLabel[e.kind]}</span>
                      <span className="block truncate text-muted" data-id="element-1163" data-mp="2qdbwyal0kajl">
                        {received(e.at)}
                        {e.note ? ` · ${e.note}` : ''}
                      </span>
                    </span>
                    <span className="shrink-0 tabular-nums text-ink" data-id="element-1164" data-mp="110zzseqsqoux">{signedAmount(e.unit, e.amount)}</span>
                  </li>)}
            </ol> : <p className="mt-4 text-[13px] text-muted" data-id="element-1165" data-mp="1500htv1g9ngv">Nog geen tegoed.</p>}
        </>}
    </Panel>;
}
type FormProps = {
  customerId: string;
  onClose: () => void;
  onDone: (next: Awaited<ReturnType<typeof correctCustomerCredit>>) => void;
};
function CorrectionForm({
  customerId,
  onClose,
  onDone
}: FormProps) {
  const amountId = useId();
  const reasonId = useId();
  const [unit, setUnit] = useState<LedgerUnit>('cents');
  const [direction, setDirection] = useState<1 | -1>(1);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const {
    submit,
    submitting,
    error,
    fieldErrors
  } = useSubmit(correctCustomerCredit);
  const raw = unit === 'cents' ? parseEuroInput(amount) : /^\d+$/.test(amount.trim()) ? Number(amount.trim()) : null;
  const amountProblem = raw === null || raw <= 0 ? unit === 'cents' ? 'Vul een bedrag in, bijv. 15 of 12,50.' : 'Vul een aantal in, bijv. 1.' : null;
  const reasonProblem = reason.trim() ? null : 'Een reden is verplicht; die staat in de geschiedenis.';
  const value = raw === null ? 0 : raw * direction;
  const attempt = `${unit}|${value}|${reason.trim()}`;
  const requestId = useRequestId(attempt);
  const amountError = fieldErrors.amount ?? (tried ? amountProblem : null);
  const reasonError = fieldErrors.reason ?? (tried ? reasonProblem : null);
  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (amountProblem) return document.getElementById(amountId)?.focus();
    if (reasonProblem) return document.getElementById(reasonId)?.focus();
    const next = await submit(customerId, {
      requestId,
      unit,
      amount: value,
      reason: reason.trim()
    });
    if (next) onDone(next);
  };
  return <form onSubmit={e => void onSubmit(e)} noValidate className="mt-4 space-y-4 rounded-md bg-shell p-3.5" data-id="element-1166" data-mp="3n5qnc97a8hnr">
      <div className="grid grid-cols-2 gap-2" data-id="element-1167" data-mp="1nvh2ps31537j">
        <label className="block" data-id="element-1168" data-mp="1ssqx7m5a0528">
          <span className="sr-only" data-id="element-1169" data-mp="1stsalx9rl3n4">Erbij of eraf</span>
          <select value={direction} onChange={e => setDirection(e.target.value === '-1' ? -1 : 1)} className={inputCls} data-id="element-1170" data-mp="2w3wm5ff1dga8">
            <option value="1" data-id="element-1171" data-mp="2r5lhdj3llvyv">Erbij</option>
            <option value="-1" data-id="element-1172" data-mp="2r6o89r0x7yes">Eraf</option>
          </select>
        </label>
        <label className="block" data-id="element-1173" data-mp="355e28xsg1hyx">
          <span className="sr-only" data-id="element-1174" data-mp="3528dxkrn2k49">Eenheid</span>
          <select value={unit} onChange={e => setUnit(e.target.value === 'treatment' ? 'treatment' : 'cents')} className={inputCls} data-id="element-1175" data-mp="304gkgmbcipxy">
            <option value="cents" data-id="element-1176" data-mp="3f0o4nmmr7rd8">Euro’s</option>
            <option value="treatment" data-id="element-1177" data-mp="3a4h8umo2nw90">Behandelingen</option>
          </select>
        </label>
      </div>
      <Field label={unit === 'cents' ? 'Bedrag (€)' : 'Aantal behandelingen'} data-id="element-1178" data-mp="3nvcabdw8rb2k">
        <input id={amountId} inputMode={unit === 'cents' ? 'decimal' : 'numeric'} value={amount} onChange={e => setAmount(e.target.value)} aria-invalid={amountError ? true : undefined} aria-describedby={amountError ? `${amountId}-error` : undefined} className={`${inputCls} tabular-nums`} data-id="element-1179" data-mp="3jtr3a96e1r0a" />
      </Field>
      {amountError && <p id={`${amountId}-error`} className="-mt-2 text-[12px] text-clay" data-id="element-1180" data-mp="la7fpq0thcpb">
          {amountError}
        </p>}
      <Field label="Reden" hint="Bijvoorbeeld: overgezet uit Salonized, of compensatie na een fout." data-id="element-1181" data-mp="6bdk2ezedbmj">
        <input id={reasonId} value={reason} onChange={e => setReason(e.target.value)} maxLength={200} aria-invalid={reasonError ? true : undefined} aria-describedby={reasonError ? `${reasonId}-error` : undefined} className={inputCls} data-id="element-1182" data-mp="ba6v80r7oqjj" />
      </Field>
      {reasonError && <p id={`${reasonId}-error`} className="-mt-2 text-[12px] text-clay" data-id="element-1183" data-mp="3oqecrdoq8bp4">
          {reasonError}
        </p>}
      {error && !fieldErrors.amount && !fieldErrors.reason && <p role="alert" className="rounded-md bg-blush/50 px-3 py-2 text-[13px] text-ink" data-id="element-1184" data-mp="3aszinvnj3fqt">
          {error}
        </p>}
      <div className="flex flex-wrap gap-2" data-id="element-1185" data-mp="3v89ano309bmw">
        <button type="submit" disabled={submitting} aria-busy={submitting} className={btnPrimary} data-id="element-1186" data-mp="3v6y8fsp7kbxw">
          {submitting && <Spinner data-id="element-1187" data-mp="3q6k2cg763c2i" />}
          {submitting ? 'Bezig…' : raw && !amountProblem ? `${signedAmount(unit, value)} opslaan` : 'Opslaan'}
        </button>
        <button type="button" onClick={onClose} disabled={submitting} className={btnSecondary} data-id="element-1188" data-mp="3gb8m1tg41wsn">
          Niet corrigeren
        </button>
      </div>
    </form>;
}